import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { Card } from '@/components/ui/card';
import { ArrowRight, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import GoalSelector from '@/components/goals/GoalSelector';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const TOTAL_STEPS = 6;

const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware',
  'District of Columbia', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
  'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota',
  'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey',
  'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon',
  'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah',
  'Vermont', 'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'
];

const COUNTRIES = [
  'United States', 'Canada', 'United Kingdom', 'Australia', 'Germany', 'France', 'Japan',
  'China', 'India', 'Brazil', 'Mexico', 'South Korea', 'Singapore', 'Other'
];

export default function OnboardingFlow() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isGenerating, setIsGenerating] = useState(false);
  const [formData, setFormData] = useState({
    // Step 1: Personal & Tax
    age: '',
    nationality: [],
    state_of_residence: '',
    tax_residence: [],
    us_tax_filing_status: '',
    other_tax_residence: '',
    
    // Step 2: Family & Income
    family_situation: '',
    dependents: '',
    annual_income: '',
    monthly_savings: '',
    income_stability: '',
    
    // Step 3: Assets & Allocation
    net_assets: '',
    liquid_assets: '',
    current_allocation: {
      stocks: 0,
      bonds: 0,
      real_estate: 0,
      crypto: 0,
      private_equity: 0,
      other: 0
    },
    max_tax_advantaged: '',
    
    // Step 4: Planning & Health
    investment_type: '',
    large_expenses: [],
    health_status: '',
    major_medical_expenses: '',
    has_insurance: '',
    emergency_fund_months: '',
    debt_total: '',
    debt_monthly: '',
    debt_types: [],
    
    // Step 5: Goals & Timeline
    investment_goal: '',
    investment_goals: [], // New: ranked goals array
    withdrawal_timeline: '',
    
    // Step 6: Knowledge & Risk
    investment_knowledge: '',
    alternative_interest: '',
    risk_chart_preference: '',
    principal_importance: '',
    accredited_investor: ''
  });

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const updateNestedField = (parent, field, value) => {
    setFormData(prev => ({
      ...prev,
      [parent]: { ...prev[parent], [field]: value }
    }));
  };

  const toggleArrayField = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(v => v !== value)
        : [...prev[field], value]
    }));
  };

  const handleSubmit = async () => {
    setIsGenerating(true);
    try {
      toast.info('Saving your profile...');
      await axios.post(`${BACKEND_URL}/api/profile`, formData, { withCredentials: true });
      
      toast.info('Generating your personalized investment plan... This may take 30-60 seconds.');
      await axios.post(`${BACKEND_URL}/api/recommendations/generate`, {}, { withCredentials: true });
      
      toast.success('Plan generated successfully!');
      navigate('/plan');
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate plan. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const progress = (currentStep / TOTAL_STEPS) * 100;

  return (
    <div className="min-h-screen bg-background py-12 px-6">
      <div className="max-w-3xl mx-auto">
        <div className="mb-8">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-muted-foreground">Step {currentStep} of {TOTAL_STEPS}</span>
            <span className="text-sm font-medium text-muted-foreground">{Math.round(progress)}%</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        <Card className="p-8 md:p-12">
          {currentStep === 1 && <Step1 formData={formData} updateField={updateField} toggleArrayField={toggleArrayField} />}
          {currentStep === 2 && <Step2 formData={formData} updateField={updateField} />}
          {currentStep === 3 && <Step3 formData={formData} updateField={updateField} updateNestedField={updateNestedField} />}
          {currentStep === 4 && <Step4 formData={formData} updateField={updateField} toggleArrayField={toggleArrayField} />}
          {currentStep === 5 && <Step5 formData={formData} updateField={updateField} />}
          {currentStep === 6 && <Step6 formData={formData} updateField={updateField} />}
        </Card>

        <div className="flex justify-between mt-8">
          <Button
            variant="outline"
            onClick={() => setCurrentStep(prev => Math.max(1, prev - 1))}
            disabled={currentStep === 1 || isGenerating}
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          {currentStep < TOTAL_STEPS ? (
            <Button onClick={() => setCurrentStep(prev => prev + 1)} disabled={isGenerating}>
              Next <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={isGenerating}>
              {isGenerating ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Generating Plan...
                </>
              ) : (
                <>
                  Generate My Plan <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// Step 1: Personal & Tax Information
function Step1({ formData, updateField, toggleArrayField }) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold mb-2">Personal & Tax Information</h2>
        <p className="text-muted-foreground">Help us understand your tax situation</p>
      </div>

      <div>
        <Label>1. How old are you?</Label>
        <Input type="number" placeholder="Enter your age" value={formData.age} onChange={(e) => updateField('age', e.target.value)} />
      </div>

      <div>
        <Label>2. What is your nationality? (Select all that apply)</Label>
        <div className="grid grid-cols-2 gap-3 mt-2">
          {COUNTRIES.map(country => (
            <div key={country} className="flex items-center space-x-2">
              <Checkbox
                id={`nat-${country}`}
                checked={formData.nationality.includes(country)}
                onCheckedChange={() => toggleArrayField('nationality', country)}
              />
              <Label htmlFor={`nat-${country}`} className="font-normal cursor-pointer">{country}</Label>
            </div>
          ))}
        </div>
      </div>

      <div>
        <Label>3. What is your current state of residence for tax purposes?</Label>
        <Select value={formData.state_of_residence} onValueChange={(val) => updateField('state_of_residence', val)}>
          <SelectTrigger><SelectValue placeholder="Select state" /></SelectTrigger>
          <SelectContent className="max-h-[300px]">
            {US_STATES.map(state => (
              <SelectItem key={state} value={state}>{state}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label>4. What is your current tax residence (countries where you pay taxes)? (Select all that apply)</Label>
        <div className="grid grid-cols-2 gap-3 mt-2">
          {COUNTRIES.map(country => (
            <div key={country} className="flex items-center space-x-2">
              <Checkbox
                id={`tax-${country}`}
                checked={formData.tax_residence.includes(country)}
                onCheckedChange={() => toggleArrayField('tax_residence', country)}
              />
              <Label htmlFor={`tax-${country}`} className="font-normal cursor-pointer">{country}</Label>
            </div>
          ))}
        </div>
      </div>

      <div>
        <Label>5. What is your U.S. tax filing status?</Label>
        <RadioGroup value={formData.us_tax_filing_status} onValueChange={(val) => updateField('us_tax_filing_status', val)}>
          <div className="flex items-center space-x-2 p-3 border rounded-lg">
            <RadioGroupItem value="citizen" id="citizen" />
            <Label htmlFor="citizen" className="cursor-pointer">U.S. Citizen / Permanent Resident (Green Card) / Resident Alien</Label>
          </div>
          <div className="flex items-center space-x-2 p-3 border rounded-lg">
            <RadioGroupItem value="nra" id="nra" />
            <Label htmlFor="nra" className="cursor-pointer">Non-Resident Alien (H-1B, L-1, student visa, etc.)</Label>
          </div>
          <div className="flex items-center space-x-2 p-3 border rounded-lg">
            <RadioGroupItem value="expat" id="expat" />
            <Label htmlFor="expat" className="cursor-pointer">U.S. Citizen/Permanent Resident living abroad (Expat)</Label>
          </div>
          <div className="flex items-center space-x-2 p-3 border rounded-lg">
            <RadioGroupItem value="foreign" id="foreign" />
            <Label htmlFor="foreign" className="cursor-pointer">Foreign National living abroad</Label>
          </div>
        </RadioGroup>
      </div>

      <div>
        <Label>6. Are you a tax resident or citizen of any other country with worldwide taxation or strict CFC/PFIC rules?</Label>
        <Input placeholder="E.g., Eritrea, or leave blank" value={formData.other_tax_residence} onChange={(e) => updateField('other_tax_residence', e.target.value)} />
      </div>
    </div>
  );
}

// Step 2: Family & Income
function Step2({ formData, updateField }) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold mb-2">Family & Income</h2>
        <p className="text-muted-foreground">Tell us about your household</p>
      </div>

      <div>
        <Label>7. What is your family situation?</Label>
        <Select value={formData.family_situation} onValueChange={(val) => updateField('family_situation', val)}>
          <SelectTrigger><SelectValue placeholder="Select family situation" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="single">Single</SelectItem>
            <SelectItem value="married">Married/Partnered</SelectItem>
            <SelectItem value="married_children">Married with children</SelectItem>
            <SelectItem value="single_parent">Single parent</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label>8. Number of dependents</Label>
        <Input type="number" placeholder="0" value={formData.dependents} onChange={(e) => updateField('dependents', e.target.value)} />
      </div>

      <div>
        <Label>9. What is your approximate annual income this year (before tax)?</Label>
        <Input type="text" placeholder="e.g., $150,000" value={formData.annual_income} onChange={(e) => updateField('annual_income', e.target.value)} />
      </div>

      <div>
        <Label>10. How much of your monthly household income is available for savings/investment?</Label>
        <Input type="text" placeholder="e.g., $3,000" value={formData.monthly_savings} onChange={(e) => updateField('monthly_savings', e.target.value)} />
      </div>

      <div>
        <Label>11. How stable is your income source?</Label>
        <Select value={formData.income_stability} onValueChange={(val) => updateField('income_stability', val)}>
          <SelectTrigger><SelectValue placeholder="Select stability" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="very_stable">Very stable (Government/Tenured)</SelectItem>
            <SelectItem value="stable">Stable (Salaried)</SelectItem>
            <SelectItem value="variable">Variable (Freelance/Commission)</SelectItem>
            <SelectItem value="unstable">Unstable</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

// Step 3: Assets & Current Allocation
function Step3({ formData, updateField, updateNestedField }) {
  const allocationTotal = Object.values(formData.current_allocation).reduce((a, b) => a + b, 0);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold mb-2">Assets & Current Allocation</h2>
        <p className="text-muted-foreground">Understanding your current financial position</p>
      </div>

      <div>
        <Label>12. Total estimated value of your net assets (Assets minus Liabilities)?</Label>
        <Input 
          type="text" 
          placeholder="e.g., $500,000 or 500000" 
          value={formData.net_assets} 
          onChange={(e) => updateField('net_assets', e.target.value)} 
        />
      </div>

      <div>
        <Label>13. Total estimated value of your liquid assets (Excluding real estate)?</Label>
        <Input 
          type="text" 
          placeholder="e.g., $250,000 or 250000" 
          value={formData.liquid_assets} 
          onChange={(e) => updateField('liquid_assets', e.target.value)} 
        />
      </div>

      <div>
        <Label>14. Current allocation of investable assets (Total: {allocationTotal}%)</Label>
        <div className="space-y-3 mt-3">
          {Object.keys(formData.current_allocation).map(key => (
            <div key={key} className="flex items-center gap-4">
              <Label className="w-32 capitalize">{key.replace('_', ' ')}</Label>
              <Select
                value={String(formData.current_allocation[key])}
                onValueChange={(val) => updateNestedField('current_allocation', key, parseInt(val))}
              >
                <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80, 85, 90, 95, 100].map(pct => (
                    <SelectItem key={pct} value={String(pct)}>{pct}%</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ))}
        </div>
        {allocationTotal !== 100 && (
          <p className="text-sm text-destructive mt-2">Total should equal 100%</p>
        )}
      </div>

      <div>
        <Label>15. Do you currently max out contributions to tax-advantaged accounts (401k, IRA)?</Label>
        <RadioGroup value={formData.max_tax_advantaged} onValueChange={(val) => updateField('max_tax_advantaged', val)}>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="yes" id="max-yes" />
            <Label htmlFor="max-yes">Yes</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="no" id="max-no" />
            <Label htmlFor="max-no">No</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="partial" id="max-partial" />
            <Label htmlFor="max-partial">Partially</Label>
          </div>
        </RadioGroup>
      </div>
    </div>
  );
}

// Step 4: Planning & Health
function Step4({ formData, updateField, toggleArrayField }) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold mb-2">Planning & Health</h2>
        <p className="text-muted-foreground">Future expenses and risk protection</p>
      </div>

      <div>
        <Label>16. Investment type preference?</Label>
        <RadioGroup value={formData.investment_type} onValueChange={(val) => updateField('investment_type', val)}>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="lump_sum" id="lump" />
            <Label htmlFor="lump">Lump sum only</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="monthly" id="monthly" />
            <Label htmlFor="monthly">Monthly contributions</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="both" id="both" />
            <Label htmlFor="both">Both</Label>
          </div>
        </RadioGroup>
      </div>

      <div>
        <Label>17. Anticipated large one-time expenses in next 10 years?</Label>
        <Input type="text" placeholder="e.g., $50,000 in 3 years for home down payment" value={formData.large_expenses} onChange={(e) => updateField('large_expenses', e.target.value)} />
      </div>

      <div>
        <Label>18. How would you describe your current health?</Label>
        <Select value={formData.health_status} onValueChange={(val) => updateField('health_status', val)}>
          <SelectTrigger><SelectValue placeholder="Select health status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="excellent">Excellent</SelectItem>
            <SelectItem value="good">Good</SelectItem>
            <SelectItem value="fair">Fair</SelectItem>
            <SelectItem value="poor">Poor</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label>Do you have any major ongoing medical expenses?</Label>
        <Input placeholder="Describe if any" value={formData.major_medical_expenses} onChange={(e) => updateField('major_medical_expenses', e.target.value)} />
      </div>

      <div>
        <Label>19. Do you have adequate life/disability insurance?</Label>
        <RadioGroup value={formData.has_insurance} onValueChange={(val) => updateField('has_insurance', val)}>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="yes" id="ins-yes" />
            <Label htmlFor="ins-yes">Yes</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="no" id="ins-no" />
            <Label htmlFor="ins-no">No</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="some" id="ins-some" />
            <Label htmlFor="ins-some">Some coverage</Label>
          </div>
        </RadioGroup>
      </div>

      <div>
        <Label>20. How many months of expenses can your emergency fund cover?</Label>
        <Input type="number" placeholder="e.g., 6" value={formData.emergency_fund_months} onChange={(e) => updateField('emergency_fund_months', e.target.value)} />
        <p className="text-xs text-muted-foreground mt-1">Recommended: At least 6 months</p>
      </div>

      <div>
        <Label>21. Total debt amount and monthly repayment</Label>
        <div className="space-y-3 mt-2">
          <Input type="text" placeholder="Total debt (e.g., $50,000)" value={formData.debt_total} onChange={(e) => updateField('debt_total', e.target.value)} />
          <Input type="text" placeholder="Monthly repayment (e.g., $1,500)" value={formData.debt_monthly} onChange={(e) => updateField('debt_monthly', e.target.value)} />
          <div className="space-y-2">
            <Label>Type of debt (Select all that apply)</Label>
            {['Credit Card', 'Student Loan', 'Mortgage', 'Car Loan', 'Personal Loan', 'Other'].map(type => (
              <div key={type} className="flex items-center space-x-2">
                <Checkbox
                  id={`debt-${type}`}
                  checked={formData.debt_types.includes(type)}
                  onCheckedChange={() => toggleArrayField('debt_types', type)}
                />
                <Label htmlFor={`debt-${type}`} className="font-normal cursor-pointer">{type}</Label>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// Step 5: Goals & Timeline
function Step5({ formData, updateField }) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold mb-2">Investment Goals & Timeline</h2>
        <p className="text-muted-foreground">What are you investing for?</p>
      </div>

      <div>
        <Label className="text-base font-medium">
          22. What are your current financial goals?
        </Label>
        <p className="text-sm text-muted-foreground mb-4">
          You can select up to 3 total goals, ranking them in terms of importance to you. 
          Select from our pre-populated goals, or create your own.
        </p>
        <GoalSelector
          value={formData.investment_goals}
          onChange={(goals) => updateField('investment_goals', goals)}
        />
      </div>

      <div>
        <Label>23. When do you realistically plan to withdraw a significant portion of this money?</Label>
        <Select value={formData.withdrawal_timeline} onValueChange={(val) => updateField('withdrawal_timeline', val)}>
          <SelectTrigger><SelectValue placeholder="Select timeline" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="<3y">Less than 3 years</SelectItem>
            <SelectItem value="3-5y">3–5 years</SelectItem>
            <SelectItem value="6-10y">6–10 years</SelectItem>
            <SelectItem value=">10y">10+ years</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

// Step 6: Knowledge & Risk Preference
function Step6({ formData, updateField }) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold mb-2">Knowledge & Risk Preference</h2>
        <p className="text-muted-foreground">Understanding your investment experience and comfort with risk</p>
      </div>

      <div>
        <Label>25. Which of the following best describes your knowledge of investments?</Label>
        <Select value={formData.investment_knowledge} onValueChange={(val) => updateField('investment_knowledge', val)}>
          <SelectTrigger><SelectValue placeholder="Select knowledge level" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">None</SelectItem>
            <SelectItem value="basic">Basic (I understand savings vs. stocks)</SelectItem>
            <SelectItem value="intermediate">Intermediate</SelectItem>
            <SelectItem value="advanced">Advanced</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label>26. Are you interested in alternative investments (cryptocurrencies)?</Label>
        <RadioGroup value={formData.alternative_interest} onValueChange={(val) => updateField('alternative_interest', val)}>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="yes" id="alt-yes" />
            <Label htmlFor="alt-yes">Yes, interested in alternatives</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="no" id="alt-no" />
            <Label htmlFor="alt-no">No, prefer traditional products only</Label>
          </div>
        </RadioGroup>
      </div>

      <div>
        <Label>27. Which chart would you prefer for your money?</Label>
        <RadioGroup value={formData.risk_chart_preference} onValueChange={(val) => updateField('risk_chart_preference', val)}>
          <div className="flex items-start space-x-2 p-4 border rounded-lg">
            <RadioGroupItem value="conservative" id="conservative" className="mt-1" />
            <div className="flex-1">
              <Label htmlFor="conservative" className="cursor-pointer font-medium">Option A: Slow, steady line</Label>
              <p className="text-sm text-muted-foreground">Gains are small, but never goes down (100% Bonds/Cash)</p>
            </div>
          </div>
          <div className="flex items-start space-x-2 p-4 border rounded-lg">
            <RadioGroupItem value="moderate" id="moderate" className="mt-1" />
            <div className="flex-1">
              <Label htmlFor="moderate" className="cursor-pointer font-medium">Option B: Wobbly line</Label>
              <p className="text-sm text-muted-foreground">Goes up and down, but ends slightly higher than A (Conservative Mix)</p>
            </div>
          </div>
          <div className="flex items-start space-x-2 p-4 border rounded-lg">
            <RadioGroupItem value="aggressive" id="aggressive" className="mt-1" />
            <div className="flex-1">
              <Label htmlFor="aggressive" className="cursor-pointer font-medium">Option C: Very jagged line</Label>
              <p className="text-sm text-muted-foreground">Big drops and big jumps, but ends significantly higher (Aggressive Growth)</p>
            </div>
          </div>
        </RadioGroup>
      </div>

      <div>
        <Label>28. How important is it that your principal investment does not decrease in value over the next 12 months?</Label>
        <Select value={formData.principal_importance} onValueChange={(val) => updateField('principal_importance', val)}>
          <SelectTrigger><SelectValue placeholder="Select importance" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="very">Very Important</SelectItem>
            <SelectItem value="somewhat">Somewhat Important</SelectItem>
            <SelectItem value="not">Not Important (I care about long-term growth)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label>29. Are you an accredited investor, qualified purchaser, or equivalent?</Label>
        <RadioGroup value={formData.accredited_investor} onValueChange={(val) => updateField('accredited_investor', val)}>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="yes" id="acc-yes" />
            <Label htmlFor="acc-yes">Yes</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="no" id="acc-no" />
            <Label htmlFor="acc-no">No</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="unsure" id="acc-unsure" />
            <Label htmlFor="acc-unsure">Not sure</Label>
          </div>
        </RadioGroup>
      </div>
    </div>
  );
}
